import React from 'react';
import { useMapCoordinates } from 'hooks/useMapCoordinates';
import Map from 'components/Map/Map';
import { calculateParkingMetrics } from 'views/ProFormaTable/helpers';
import { IProFormaValues } from 'views/ProFormaTable/types';
import { numeralFormatter } from 'ns_libs/formatter';
import ExecutiveSummaryCard from '../ExecutiveSummaryCard/ExecutiveSummaryCard';
import { useProFormaSocketContext } from '../../ProForma/socketContext/ProFormaSocketProvider';

const ExecutiveSummaryDetail = () => {
    const { proForma, incomeSummary, buildableLots } = useProFormaSocketContext();

    const totalUnits = incomeSummary?.totalUnits || 0;
    const totalRentableSf = incomeSummary?.totalRentableSf || 0;

    const buildings = proForma?.buildings || 0;
    const stories = proForma?.averageStories || 0;
    const address = [
        proForma?.address,
        proForma?.city,
        proForma?.state,
        proForma?.zipCode,
    ].filter(Boolean).join(', ');
    const description = proForma?.description || '';
    const grossBuiltSf = proForma?.builtGrossSquareFootage ? Number(proForma.builtGrossSquareFootage) : 0;
    const efficiencyRatio = totalRentableSf && grossBuiltSf
        ? numeralFormatter(((totalRentableSf / grossBuiltSf) * 100), '0,0.00')
        : undefined;

    const proFormaValues: IProFormaValues = {
        ...proForma,
        landArea: buildableLots.totalLandAreaSquareFootage || null,
        floorAreaRatio: buildableLots.totalFar || null,
        grossBuildableArea: buildableLots.totalGrossBuildableArea || null,
        builtGrossSquareFootage: proForma?.builtGrossSquareFootage ? Number(proForma.builtGrossSquareFootage) : null,
        efficiencyRatio: proForma?.efficiencyRatio != null ? Number(proForma.efficiencyRatio) * 100 : null,
        estimatedUnits: proForma?.estimatedUnits != null ? Number(proForma.estimatedUnits) : null,
        estimatedRacks: proForma?.estimatedRacks != null ? Number(proForma.estimatedRacks) : null,
    };

    const { totalSpots: parkingSpaces, ratio: parkingRatio } = calculateParkingMetrics(proFormaValues, totalUnits, proForma.proFormaModelId);

    const { lat, lng } = useMapCoordinates(proForma ? {
        address: proForma.address ?? undefined,
        city: proForma.city ?? undefined,
        state: proForma.state ?? undefined,
        zipCode: proForma.zipCode ?? undefined,
        latitude: proForma.latitude ?? undefined,
        longitude: proForma.longitude ?? undefined,
    } : null);

    const detailSections = [
        {
            id: 'details',
            title: 'Details',
            items: [
                { id: 'address', label: 'Address', value: address },
                {
                    id: 'landArea',
                    label: 'Land area',
                    value: `${numeralFormatter(buildableLots.totalLandAreaSquareFootage, '0,0.[00]')} SF`,
                },
                { id: 'far', label: 'FAR', value: buildableLots.totalFar ? numeralFormatter(buildableLots.totalFar, '0,0.00') : '—' },
                {
                    id: 'grossBuildableArea',
                    label: 'Gross buildable area',
                    value: `${numeralFormatter(buildableLots.totalGrossBuildableArea, '0,0.[00]')} SF`,
                },
                { id: 'buildings', label: 'Buildings', value: numeralFormatter(buildings) },
                { id: 'stories', label: 'Stories', value: numeralFormatter(stories) },
                { id: 'units', label: 'Units', value: numeralFormatter(totalUnits) },
                { id: 'grossBuiltSf', label: 'Gross Built SF', value: `${numeralFormatter(grossBuiltSf)} GSF` },
                { id: 'netRentableArea', label: 'Net Rentable Area', value: `${numeralFormatter(totalRentableSf)} RSF` },
                { id: 'efficiencyRatio', label: 'Efficiency ratio', value: efficiencyRatio ? `${efficiencyRatio}%` : '—' },
                { id: 'parkingSpaces', label: 'Parking spaces', value: numeralFormatter(parkingSpaces) },
                { id: 'parkingRatio', label: 'Parking ratio', value: parkingRatio ? `${parkingRatio} / unit` : 'N/A' },
            ],
        },
    ];

    if (description && description.trim()) {
        detailSections.push({
            id: 'description',
            title: 'Description',
            items: [
                {
                    id: 'description',
                    value: description,
                    label: 'Description',
                },
            ],
        });
    }

    return (
        <div className="d-flex justify-content-between align-items-stretch mb-3">
            <ExecutiveSummaryCard containerClassName="w-50 mr-1" sections={detailSections} />
            <div className="ml-1 w-50 d-flex position-relative">
                <Map lat={lat} lng={lng} height="100%" />
            </div>
        </div>
    );
};

export default ExecutiveSummaryDetail;
